import { useCustom, useNavigation } from "@refinedev/core";
import { List } from "@refinedev/antd";
import { Button, Input, Select, Space, Table, Tag } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { apiUrl } from "@/consts";
import type { SurveySubmissionRow, SurveySubmissionStatus } from "./types";
import { PlayerCardModal } from "@/pages/scoutingEvents/components/PlayerCardModal";

const PAGE_SIZE = 100;

const STATUS_COLOR: Record<SurveySubmissionStatus, string> = {
  NOT_SUBMITTED: "default",
  PENDING_REVIEW: "gold",
  ACCEPTED: "success",
  REJECTED: "red",
};

const STATUS_OPTIONS: { value: SurveySubmissionStatus; label: string }[] = [
  { value: "NOT_SUBMITTED", label: "Not submitted" },
  { value: "PENDING_REVIEW", label: "Pending review" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "REJECTED", label: "Rejected" },
];

export function SurveySubmissionsList() {
  const { show } = useNavigation();
  const [page, setPage] = useState(1);
  const [eventIdInput, setEventIdInput] = useState("");
  const [eventIdFilter, setEventIdFilter] = useState<string | undefined>();
  const [status, setStatus] = useState<SurveySubmissionStatus | undefined>();

  const applyEventIdFilter = () => {
    const v = eventIdInput.trim() || undefined;
    setEventIdFilter(v);
    setPage(1);
  };

  const listQuery = useMemo(() => {
    const q: Record<string, string> = {
      page: String(page),
      limit: String(PAGE_SIZE),
    };
    if (eventIdFilter) q.eventId = eventIdFilter;
    if (status) q.status = status;
    return { query: q };
  }, [page, eventIdFilter, status]);

  const { data, isLoading, isFetching } = useCustom<{
    submissions: SurveySubmissionRow[];
    total: number;
  }>({
    url: `${apiUrl}/survey-submissions`,
    method: "get",
    config: listQuery,
  });

  const submissions = data?.data?.submissions ?? [];
  const total = data?.data?.total ?? 0;

  const [cardModal, setCardModal] = useState<{
    open: boolean;
    eventId?: string;
    userId?: string;
    playerName?: string | null;
    playerEmail?: string | null;
  }>({ open: false });

  return (
    <List
      title="Survey submissions"
      resource="survey-submissions"
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          placeholder="Filter by event ID"
          style={{ minWidth: 240 }}
          value={eventIdInput}
          onChange={(e) => {
            setEventIdInput(e.target.value);
          }}
          onPressEnter={applyEventIdFilter}
          onBlur={applyEventIdFilter}
        />
        <Select
          allowClear
          placeholder="All statuses"
          style={{ minWidth: 180 }}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          options={STATUS_OPTIONS}
        />
      </Space>
      <Table<SurveySubmissionRow>
        loading={isLoading || isFetching}
        dataSource={submissions}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showTotal: (t) => `${t} submissions`,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        columns={[
          {
            title: "Player",
            key: "player",
            render: (_, r) => r.player?.fullName || r.player?.username || r.userId,
          },
          {
            title: "Event",
            dataIndex: "event",
            render: (ev: SurveySubmissionRow["event"]) => ev?.name ?? "—",
          },
          {
            title: "Status",
            dataIndex: "status",
            width: 140,
            render: (s: SurveySubmissionStatus) => (
              <Tag color={STATUS_COLOR[s] ?? "default"}>{s}</Tag>
            ),
          },
          {
            title: "Submitted",
            dataIndex: "submittedAt",
            width: 200,
            render: (v: string | null) =>
              v ? new Date(v).toLocaleString() : "—",
          },
          {
            title: "Actions",
            key: "actions",
            width: 220,
            render: (_: unknown, r: SurveySubmissionRow) => (
              <Space size={4}>
                <Button type="link" onClick={() => show("survey-submissions", r.id)}>
                  Open
                </Button>
                {r.status === "ACCEPTED" ? (
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() =>
                      setCardModal({
                        open: true,
                        eventId: r.eventId,
                        userId: r.userId,
                        playerName: r.player?.fullName ?? null,
                        playerEmail: r.player?.email ?? null,
                      })
                    }
                  >
                    View card
                  </Button>
                ) : null}
              </Space>
            ),
          },
        ]}
      />
      <PlayerCardModal
        open={cardModal.open}
        onClose={() => setCardModal({ open: false })}
        eventId={cardModal.eventId}
        userId={cardModal.userId}
        playerName={cardModal.playerName}
        playerEmail={cardModal.playerEmail}
      />
    </List>
  );
}
